import { fetchAccumulatedOfferCommissionById } from 'actions/AgentHubActions'
import { useEffect } from 'react'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { max } from 'lib/array/arrayUtils'
import { getAgentHubOfferCommissionBody, getAgentHubOfferCommissionBodyForBundledFlow } from 'agentHub/selectors/agentHubSelectors'
import { fetchAccumulatedOfferById } from 'actions/OfferActions'
import config from 'constants/config'
import { mapOfferToAgentHubCommissionPayload } from 'agentHub/api/mappers/commissionMap'
import { getCheckInDateFromURLSearchParams, getCheckOutDateFromURLSearchParams, getOccupanciesFromURLSearchParams } from 'lib/url/searchUrlUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useQueryParams from 'hooks/useQueryParams'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { mapBedbankPackageToAgentHubPayload } from 'agentHub/api/mappers/agentHubMap'

interface UseAgentHubCommissionDateOverrides {
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
}

function useAgentHubCommission(
  offerId: string,
  /**
   * @param offer - optional, to be used when the offer is not found by redux store (e.g. car hire and experience offers)
   */
  offer?: App.AgentHubOfferPayload,
  /**
   * @param dateOverrides - optional, when provided these values are used instead of URL query params
   * for bedbank rate key resolution and commission fetch parameters (e.g. tour accommodation step where
   * dates come from component state rather than URL).
   */
  dateOverrides?: UseAgentHubCommissionDateOverrides,
) {
  const dispatch = useAppDispatch()

  const offerSummary = useAppSelector(state => state.offer.offerSummaries[offerId] || state.offer.bedbankOfferSummaries[offerId] || state.offer.tourV2Offers[offerId] || state.offer.offers[`cruise:${offerId}`])

  const agentHubOfferCommissionBody = useAppSelector(state => getAgentHubOfferCommissionBody(state, offerId))

  const mappedOfferParameter = offer && 'type' in offer && offer.type !== 'bedbank_hotel' ? mapOfferToAgentHubCommissionPayload(offer) : null

  const flightWithJourneyCommissionBody = useAppSelector(state => offer && 'journey' in offer ? getAgentHubOfferCommissionBodyForBundledFlow(state, offer) : null)

  const mappedAgentHubOfferCommission = agentHubOfferCommissionBody ?? mappedOfferParameter ?? flightWithJourneyCommissionBody

  const isFetchRequired = config.agentHub.isEnabled && (offerSummary || offer) && !mappedAgentHubOfferCommission

  const offerCommissionData = useAppSelector(state => state.agentHub.offersCommissionMap?.[offerId])

  const commissionPercentage = offerCommissionData?.commissionPercentage ?? 0
  const packagesCommission = offerCommissionData?.packages ?? []
  const minBaseCommission = offerCommissionData?.minBaseCommission || commissionPercentage
  const maxBaseCommission = offerCommissionData?.maxBaseCommission || commissionPercentage
  const greatestPackageCommission = max(packagesCommission, pkg => pkg.commissionPercentage)?.commissionPercentage ?? 0
  const dateRangesCommission = offerCommissionData?.dateRangesCommission ?? {}
  const bedbankOffer = useAppSelector(state => state.offer.bedbankOfferRates?.[offerId]) || {}

  useEffect(() => {
    if (isFetchRequired) {
      dispatch(fetchAccumulatedOfferById(offerId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, isFetchRequired])

  const queryParams = useQueryParams()
  const hasDateOverrides = !!(dateOverrides?.checkIn && dateOverrides?.checkOut && dateOverrides?.rooms)

  useEffect(() => {
    if (config.agentHub.isEnabled && mappedAgentHubOfferCommission) {
      let startDate: string | undefined
      let endDate: string | undefined
      let occupancies: Array<App.Occupants> | undefined

      if (hasDateOverrides) {
        startDate = dateOverrides.checkIn
        endDate = dateOverrides.checkOut
        occupancies = dateOverrides.rooms
      } else {
        const checkInParam = getCheckInDateFromURLSearchParams(queryParams)
        const checkOutParam = getCheckOutDateFromURLSearchParams(queryParams)
        occupancies = getOccupanciesFromURLSearchParams(queryParams)
        startDate = checkInParam?.format(ISO_DATE_FORMAT)
        endDate = checkOutParam?.format(ISO_DATE_FORMAT)
      }

      const bodyToSend =
        agentHubOfferCommissionBody?.type === 'bedbank_hotel' && bedbankOffer && occupancies && startDate && endDate
          ? {
              ...agentHubOfferCommissionBody,
              packages: (bedbankOffer[getBedbankRateKey(occupancies, startDate, endDate)] || []).map(mapBedbankPackageToAgentHubPayload),
            }
          : mappedAgentHubOfferCommission

      dispatch(fetchAccumulatedOfferCommissionById(offerId, bodyToSend, {
        startDate,
        endDate,
        occupants: occupancies,
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentHubOfferCommissionBody, hasDateOverrides, dateOverrides?.checkIn, dateOverrides?.checkOut, dateOverrides?.rooms, queryParams.get('checkIn'), queryParams.get('checkOut'), queryParams.get('occupancies')])

  return { commissionPercentage, packagesCommission, greatestPackageCommission, minBaseCommission, maxBaseCommission, dateRangesCommission }
}

export default useAgentHubCommission
